// Pass Map — a player's passes on a rotated pitch, ported from js/renderers-shotmap.js
// (renderPassMapModule, drawPasses, wireMagneticPassHover, renderPassTable). The shared
// shell (strip, stats bar, empty states, chips, Colour-by, table toggle) is EventMapBody —
// the legacy renderEventModule, shared with Shot Map.
//
// needsPlayer:true, not mergeable. A season is ~1,300 overlapping lines, so (04l-e fork 1A):
// - HOVER is magnetic (nearest line by point-to-segment distance — a 0.25-wide line can't
//   be hit-tested natively) and runs on every mousemove, so ONE effect does it by direct
//   class toggles (`.event-pass-selected` / `.event-pass-dimmed`) + the shared tooltip;
// - the hovered pass also drives the Pass Details panel and the ball. That changes only
//   when the hovered LINE changes, so it IS React state — and the lines JSX is memoised,
//   so a hover re-renders the panel without React walking the 1,300 lines.
//
// The link (catalyst:event-highlight / catalyst:event-selection, by pass id) with Passing
// Profile and any other Pass Map: we broadcast our hovered pass, and — with no local hover —
// show another widget's hovered pass (the echo) or a clicked wedge's group. Like the legacy,
// that received state lives with the listeners and starts empty on every re-draw of the
// lines (04l-e fork 2A: a filter change or a zoom drops a group highlight).
//
// Drag-to-zoom: the shared usePitchAreaZoom hook (as Shot Map).
//
// Instance state (the legacy keys, so saveState/undo capture them): `_eventFilters` (shared
// with Shot Map + Passing Profile), `_eventMapView`, `_passColorBy`, `_eventAreaSelection`,
// and the kit's `_eventMapCompetitionId`/`_eventMapSeason`.

import { useEffect, useMemo, useReducer, useRef, useState } from "react";
import * as d3 from "d3";
import type { ProtoEvent } from "@catbento/shared";
import type { ProtoModuleProps } from "./bridge.js";
import { TeamMap, showsTeamEvents } from "./TeamMap.js";
import { EventMapModule, useGradientPrefix, type EventModuleConfig, type EventPitchProps } from "./EventMapBody.js";
import { EventDetailLayout } from "./EventDetailParts.js";
import { PassDetailsPanel } from "./PassDetailsPanel.js";
import { PitchMarkings } from "./PitchMarkings.js";
import { type SortColumn } from "./sortable-rows.js";
import { useSortableRows } from "./useSortableRows.js";
import { usePitchAreaZoom } from "./usePitchAreaZoom.js";
import {
  hoverLineFlags,
  nextHoveredPass,
  passLine,
  passStats,
  passSummary,
  inSummaryRow,
  passTableRows,
  passiveLineFlags,
  resolvePassiveState,
  type PassLine,
  type PassSummaryRow,
  type PassSummaryKey,
  type PassTableRow,
} from "./passmap-view.js";
import { PASS_COLOR_MODES, paintOrder, passFilters, pitchZoomView, type EventContext } from "../engines/event-map-kit.js";
import {
  broadcastEventHighlight,
  broadcastEventLock,
  lockedEvent,
  subscribeEventHighlight,
  subscribeEventLock,
} from "../engines/event-link.js";
import { hideTooltip, showTooltipAt } from "../canvas/tooltip.js";
import { startMatchDrag } from "../canvas/match-drag.js";
import { ZONE_COLS, ZONE_H, ZONE_ROWS, ZONE_W, zoneOf } from "../live/event-focus.js";
import { PITCH_L, PITCH_W } from "../engines/event-map-kit.js";
import { LIVE } from "../live/flag.js";
import { getModState, setModState } from "./bridge.js";
import { undoApi } from "../canvas/undo.js";

const PASS_CFG: EventModuleConfig = {
  key: "passes",
  live: true, // real data on ?live=1 (T2-7)
  emptyNoun: "pass",
  pluralNoun: "passes",
  filters: passFilters,
  colorModes: PASS_COLOR_MODES,
  colorStateKey: "_passColorBy",
  defaultColor: "result",
  stats: (filtered, events) => {
    const stats = passStats(filtered, events);
    return (
      <>
        <div className="event-stat"><span className="event-stat-value">{stats.shown}</span><span className="event-stat-label">passes shown</span></div>
        <div className="event-stat"><span className="event-stat-value">{stats.success}</span><span className="event-stat-label">success</span></div>
      </>
    );
  },
  Pitch: PassPitch,
  Table: PassTable,
};

export function PassMapModule(props: ProtoModuleProps) {
  // A match dropped on it (teams and matches, Ben 2026-10-30): the match's passes, a team at a time;
  // a team on it (Ben, 2026-11-02): its season's passes, its own or its opponents'.
  if (LIVE && showsTeamEvents(props.mod)) return <TeamMap {...props} cfg={PASS_CFG} />;
  return <EventMapModule {...props} cfg={PASS_CFG} />;
}

// --- the pitch + details panel --------------------------------------------------------------

interface ColoredLine extends PassLine {
  rgb: string;
}

/** What the panel (and ball) show: a local hover gets the ball on a completed pass; an echo
 *  from another widget fills the panel only. */
interface Shown {
  line: ColoredLine;
  ball: boolean;
}

function PassPitch({ mod, ctx, events: passes, colorMode, area, onArea, zones = null, linkedIds = null, onOwnPick, below, zonesOnly = false }: EventPitchProps) {
  const gradPrefix = useGradientPrefix();
  const svgRef = useRef<SVGSVGElement>(null);
  usePitchAreaZoom(svgRef, onArea);
  const [shown, setShown] = useState<Shown | null>(null);

  // Live zones (Ben, 2026-10-19): with zones picked, a pass starting outside them is faded and
  // can't be hovered.
  const picked = zones?.selected ?? [];
  const zoneKey = picked.join(",");
  // A team's season on zones draws only the picked zones' passes (EventPitchProps.zonesOnly).
  const thin = zonesOnly && zones !== null;
  // Extreme values paint last (on top) for a continuous mode; categorical modes keep the
  // data's chronological order.
  const lines = useMemo<ColoredLine[]>(
    () =>
      paintOrder(thin ? passes.filter((p) => picked.includes(zoneOf(p.x, p.y))) : passes, colorMode).map((p) => ({
        ...passLine(ctx, p),
        rgb: colorMode.getColor(p),
      })),
    // zoneKey stands for `picked` (a new array each render).
    [passes, colorMode, ctx, thin, zoneKey],
  );
  // Live: a Summary row clicked (Ben, 2026-10-20) — only its passes stay bright, as a zone.
  const [, rerender] = useReducer((x: number) => x + 1, 0);
  const rowPick = LIVE ? (getModState<PassSummaryKey>(mod, "_passSummaryPick") ?? null) : null;
  const pickRow = (key: PassSummaryKey) => {
    undoApi().saveState();
    // Total (Ben, 2026-10-20) — or the picked row again — shows every pass again.
    setModState(mod, "_passSummaryPick", key === "total" || rowPick === key ? undefined : key);
    // The shared selection: the Passing Report and Passing Profile follow (EventMapBody's announce).
    onOwnPick?.();
    rerender();
  };
  const fadeKey = `${picked.join(",")}|${rowPick ?? ""}`;
  const ownPick = (p: ProtoEvent) =>
    (picked.length === 0 || picked.includes(zoneOf(p.x, p.y))) && (rowPick === null || inSummaryRow(rowPick, p));
  /** A pass outside the picked zones, the picked Summary row or another module's pick (the shared
   *  selection, Ben 2026-10-21): faded, can't be hovered. */
  const isOut = (p: ProtoEvent) => !ownPick(p) || (linkedIds !== null && !linkedIds.has(p.id));
  const zoneOut = (ld: ColoredLine) => isOut(ld.pass);
  // Live: the Summary in the panel while no pass is hovered (Ben, 2026-10-20 — Catalyst's Summary
  // tile), over the passes shown: the picked zones' only, when zones are picked.
  const summary = useMemo(
    () =>
      LIVE
        ? passSummary(passes.filter((p) => (picked.length === 0 || picked.includes(zoneOf(p.x, p.y))) && (linkedIds === null || linkedIds.has(p.id))))
        : null,
    // zoneKey stands for `picked` (a new array each render).
    [passes, zoneKey, linkedIds],
  );
  // Passes per zone (where they start), for the grid's shading + numbers.
  const zoneCounts = useMemo(() => {
    const counts = new Array<number>(ZONE_COLS * ZONE_ROWS).fill(0);
    for (const p of passes) counts[zoneOf(p.x, p.y)]!++;
    return counts;
  }, [passes]);

  // Memoised elements: a hover changes `shown`, and React skips an identical element, so it
  // never re-walks the lines (their hover classes are the effect's, not React's).
  const gradients = useMemo(
    () =>
      lines.map((ld, i) => (
        <linearGradient key={ld.pass.id} id={`${gradPrefix}-${i}`} gradientUnits="userSpaceOnUse" x1={ld.x1} y1={ld.y1} x2={ld.x2} y2={ld.y2}>
          <stop offset="0%" stopColor={ld.rgb} stopOpacity="0.04"></stop>
          <stop offset="100%" stopColor={ld.rgb} stopOpacity="0.75"></stop>
        </linearGradient>
      )),
    [lines, gradPrefix],
  );
  const lineEls = useMemo(
    () =>
      lines.map((ld, i) => (
        <line
          key={ld.pass.id}
          x1={ld.x1}
          y1={ld.y1}
          x2={ld.x2}
          y2={ld.y2}
          className={`event-pass-line ${ld.completed ? "complete" : "incomplete"}${zoneOut(ld) ? " event-pass-zone-out" : ""}`}
          stroke={`url(#${gradPrefix}-${i})`}
        ></line>
      )),
    [lines, gradPrefix, fadeKey, linkedIds],
  );

  // With zones on, a click picks a zone, not a pass (a pass clicked elsewhere still shows here).
  const zonesOnRef = useRef(false);
  zonesOnRef.current = zones !== null;

  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;
    // The CTM must come from the rotated group itself — the lines live in its local space.
    const rotateGroup = svg.querySelector<SVGGElement>(".pitch-rotate");
    const layer = svg.querySelector(".event-layer");
    if (!rotateGroup || !layer) return;
    // A re-draw starts with an empty panel, as the legacy's rebuilt one did.
    setShown(null);
    // React renders the lines in `lines` order, so child i is line i.
    const all = lines.map((ld, i) => ({ ...ld, el: layer.children[i] as SVGLineElement }));
    // Hover only reaches the bright passes (all of them when nothing is picked).
    const entries = all.filter((e) => !e.el.classList.contains("event-pass-zone-out"));
    const byId = new Map(entries.map((e) => [e.pass.id, e]));
    const byIdAll = new Map(all.map((e) => [e.pass.id, e]));
    let selected: (typeof entries)[number] | null = null;
    // A clicked pass (here or on a Passing Profile — event-link's lock, Ben 2026-10-20): it holds
    // the highlight and the panel; hover waits until it's cleared.
    let locked: (typeof all)[number] | null = null;
    // The received link state (lower priority than our own hover) — kept apart so clearing
    // one never wipes the other.
    let echoedId: string | null = null;

    const applyFlags = (flagsFor: (id: string) => { selected: boolean; dimmed: boolean }) => {
      for (const e of entries) {
        const f = flagsFor(e.pass.id);
        e.el.classList.toggle("event-pass-selected", f.selected);
        e.el.classList.toggle("event-pass-dimmed", f.dimmed);
      }
    };
    const applyPassiveState = () => {
      const st = resolvePassiveState(echoedId, null, (id) => byId.has(id));
      applyFlags((id) => passiveLineFlags(id, st));
      const match = st.kind === "echo" && st.matchId != null ? byId.get(st.matchId) : undefined;
      setShown(match ? { line: match, ball: false } : null);
    };
    const showLock = (id: string | null) => {
      locked = id === null ? null : (byIdAll.get(id) ?? null);
      selected = null;
      hideTooltip();
      if (locked) {
        const l = locked;
        applyFlags((x) => hoverLineFlags(x, l.pass.id));
        setShown({ line: l, ball: l.completed });
      } else applyPassiveState();
    };
    const setSelected = (entry: typeof selected, e?: MouseEvent) => {
      if (locked) return;
      if (selected !== entry) {
        selected = entry;
        if (entry) applyFlags((id) => hoverLineFlags(id, entry.pass.id));
        else applyPassiveState();
        // Every other open widget showing this pass highlights it too (our own listener
        // hears this as well — ignored while we hold a local hover).
        broadcastEventHighlight(entry ? entry.pass.id : null);
        // The ball only while a COMPLETED pass is hovered — an incomplete one reached no one.
        if (entry) setShown({ line: entry, ball: entry.completed });
      }
      if (entry && e) showTooltipAt(entry.tooltip, e.clientX, e.clientY, "below");
      else hideTooltip();
    };

    const offHighlight = subscribeEventHighlight((id) => {
      echoedId = id;
      if (selected || locked) return;
      applyPassiveState();
    });
    const offLock = subscribeEventLock(showLock);
    if (lockedEvent() !== null) showLock(lockedEvent());

    const onMove = (e: MouseEvent) => {
      // No hover while an area-zoom drag is pending or live (usePitchAreaZoom's DOM flags).
      if (svg.dataset.areaDragging === "1" || svg.dataset.areaPointerDown === "1") return;
      const ctm = rotateGroup.getScreenCTM();
      if (!ctm) return;
      const pt = svg.createSVGPoint();
      pt.x = e.clientX;
      pt.y = e.clientY;
      setSelected(nextHoveredPass(pt.matrixTransform(ctm.inverse()), entries, selected), e);
    };
    const onLeave = () => setSelected(null);
    // Click a pass → it's selected here and on every open Passing Profile; the same pass again, or
    // anywhere while one is selected, clears it. Not the end of a zoom drag; not with zones on.
    const onClick = (e: MouseEvent) => {
      if (zonesOnRef.current || svg.dataset.areaDragging === "1") return;
      const ctm = rotateGroup.getScreenCTM();
      if (!ctm) return;
      const pt = svg.createSVGPoint();
      pt.x = e.clientX;
      pt.y = e.clientY;
      const hit = nextHoveredPass(pt.matrixTransform(ctm.inverse()), entries, null);
      if (!hit && !locked) return;
      broadcastEventHighlight(null);
      broadcastEventLock(hit && hit.pass.id !== locked?.pass.id ? hit.pass.id : null);
    };

    svg.addEventListener("mousemove", onMove);
    svg.addEventListener("mouseleave", onLeave);
    svg.addEventListener("click", onClick);
    return () => {
      svg.removeEventListener("mousemove", onMove);
      svg.removeEventListener("mouseleave", onLeave);
      svg.removeEventListener("click", onClick);
      offHighlight();
      offLock();
      // React reuses the same <line> nodes across a re-draw — drop our classes first.
      for (const e of entries) e.el.classList.remove("event-pass-selected", "event-pass-dimmed");
    };
  }, [lines, fadeKey, linkedIds]);

  // Zones: a click picks a zone (Shift adds one); pressing a picked zone and moving drags the
  // picked zones out (onto a Heat Map: where those passes ended). The press is caught before the
  // drag-to-zoom sees it, so it doesn't also draw a zoom box.
  const zonesRef = useRef(zones);
  zonesRef.current = zones;
  const zonesOn = zones !== null;
  useEffect(() => {
    const svg = svgRef.current;
    if (!svg || !zonesOn) return;
    const zoneAt = (e: MouseEvent): number | null => {
      const ctm = svg.querySelector<SVGGElement>(".pitch-rotate")?.getScreenCTM();
      if (!ctm) return null;
      const pt = svg.createSVGPoint();
      pt.x = e.clientX;
      pt.y = e.clientY;
      const p = pt.matrixTransform(ctm.inverse());
      return p.x < 0 || p.x > PITCH_L || p.y < 0 || p.y > PITCH_W ? null : zoneOf(p.x, p.y);
    };
    let pressedPicked = false;
    let moved = false;
    const onDown = (e: PointerEvent) => {
      pressedPicked = false;
      moved = false;
      const z = zonesRef.current;
      if (e.button !== 0 || !z) return;
      const zone = zoneAt(e);
      if (zone === null || !z.selected.includes(zone) || !z.focus) return;
      pressedPicked = true;
      e.stopImmediatePropagation();
      e.preventDefault();
      hideTooltip();
      const x0 = e.clientX;
      const y0 = e.clientY;
      const onMove = (ev: PointerEvent) => {
        if (Math.hypot(ev.clientX - x0, ev.clientY - y0) >= 5) moved = true;
      };
      window.addEventListener("pointermove", onMove);
      window.addEventListener("pointerup", () => window.removeEventListener("pointermove", onMove), { once: true });
      startMatchDrag(e, mod, z.focus);
    };
    const onClick = (e: MouseEvent) => {
      // The end of a zoom drag, or of a zone drag, isn't a click.
      if (svg.dataset.areaDragging === "1" || (pressedPicked && moved)) return;
      const zone = zoneAt(e);
      if (zone !== null) zonesRef.current?.onPick(zone, e.shiftKey);
    };
    svg.addEventListener("pointerdown", onDown, true);
    svg.addEventListener("click", onClick);
    return () => {
      svg.removeEventListener("pointerdown", onDown, true);
      svg.removeEventListener("click", onClick);
    };
  }, [zonesOn, mod]);

  const zoneMax = Math.max(1, ...zoneCounts);
  const zoneLayer = zones && (
    <g className={`event-zone-layer${zones.selected.length ? " has-picked" : ""}`}>
      {zoneCounts.map((count, i) => {
        const x = (i % ZONE_COLS) * ZONE_W;
        const y = Math.floor(i / ZONE_COLS) * ZONE_H;
        const on = zones.selected.includes(i);
        return (
          <g key={i} className={`event-zone${on ? " picked" : ""}`}>
            {/* The Heat Map grid's colours (yellow → red, Ben 2026-10-20: the green blurred into the
                green passes); sqrt so one busy zone doesn't wash the rest out, as there. */}
            <rect x={x} y={y} width={ZONE_W} height={ZONE_H} style={count ? { fill: d3.interpolateYlOrRd(Math.sqrt(count / zoneMax)) } : { fillOpacity: 0 }} />
            {/* The pitch group is turned a quarter; the number turns back to read upright. */}
            <text x={x + ZONE_W / 2} y={y + ZONE_H / 2} transform={`rotate(90 ${x + ZONE_W / 2} ${y + ZONE_H / 2})`}>
              {count}
            </text>
          </g>
        );
      })}
    </g>
  );

  const zoom = pitchZoomView(area);
  const pitch = (
    <div className="event-pitch-wrap">
      <svg
        ref={svgRef}
        className={`event-pitch-svg event-pitch-svg-rotated${zoom.zoomed ? " event-zoomed" : ""}${zones ? " zones-on" : ""}`}
        viewBox={zoom.viewBox}
        preserveAspectRatio="xMidYMid meet"
        // Inline only while zoomed, so the stylesheet's static 74:114 rule wins otherwise.
        style={zoom.aspectRatio ? { aspectRatio: zoom.aspectRatio } : undefined}
      >
        {/* A per-line gradient (faint at the start, fuller at the end) shows direction
            without an arrowhead. */}
        <defs className="event-defs">{gradients}</defs>
        <g className="pitch-rotate" transform={zoom.transform}>
          <g className="pitch-frame">
            <PitchMarkings />
          </g>
          {zoneLayer}
          <g className="event-layer">{lineEls}</g>
          <g className="freeze-frame-layer">
            {/* r = half the SELECTED line's 0.6 stroke — the ball only exists while its line is. */}
            {shown?.ball && <circle cx={shown.line.x2} cy={shown.line.y2} r="0.3" className="event-pass-ball"></circle>}
          </g>
        </g>
      </svg>
      {area && (
        <button type="button" className="event-area-reset-btn" onClick={() => onArea(null)}>
          Reset view
        </button>
      )}
    </div>
  );

  return (
    <EventDetailLayout mod={mod} pitch={pitch} below={below} header={shown || !summary ? "Pass Details" : picked.length ? `Summary · ${picked.length === 1 ? "1 zone" : `${picked.length} zones`}` : "Summary"}>
      {shown ? (
        <PassDetailsPanel ctx={ctx} pass={shown.line.pass} />
      ) : summary ? (
        <PassSummaryTable rows={summary} picked={rowPick} onPick={pickRow} />
      ) : (
        <div className="event-details-empty">Hover a pass to see its details</div>
      )}
    </EventDetailLayout>
  );
}

/** Catalyst's Summary table: name | value | share of the total. */
function PassSummaryTable({ rows, picked, onPick }: { rows: PassSummaryRow[]; picked: PassSummaryKey | null; onPick: (key: PassSummaryKey) => void }) {
  return (
    <>
      <table className="pass-summary-table">
        <tbody>
          {rows.map((r) => (
            <tr
              key={r.label}
              className={[r.groupStart && "group-start", r.key && "pickable", r.key && r.key === (picked ?? "total") && "picked"].filter(Boolean).join(" ") || undefined}
              title={r.key ? (r.key === "total" || r.key === picked ? "Show every pass" : "Highlight only these on the pitch") : undefined}
              onClick={r.key ? () => onPick(r.key!) : undefined}
            >
              <td>{r.label}</td>
              <td className="num">{r.value}</td>
              <td className="share">{r.share}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="pass-summary-hint">Click a row to highlight those passes · hover a pass for its details</div>
    </>
  );
}

// --- the table ----------------------------------------------------------------------------

// Only Min sorts (by its data-val minute) — the rest are the legacy's `.no-sort` columns.
const PASS_COLUMNS: SortColumn<PassTableRow>[] = [
  { label: "Opposition", get: (r) => r.opposition, sortable: false },
  { label: "Min", get: (r) => r.minute },
  { label: "Type", get: (r) => r.type, sortable: false },
  { label: "Body Part", get: (r) => r.bodyPart, sortable: false },
  { label: "Result", get: (r) => r.result, sortable: false },
];

function PassTable({ ctx, events: passes }: { ctx: EventContext; events: ProtoEvent[] }) {
  const source = useMemo(() => passTableRows(ctx, passes), [ctx, passes]);
  const { rows, headerProps, rankProps, containerRef } = useSortableRows(source, PASS_COLUMNS);
  return (
    <div className="data-wrapper top-align event-table" ref={containerRef}>
      <div className="data-row header">
        <div className="col-rank" onClick={rankProps.onClick}>#</div>
        <div className="col-team no-sort">Opposition</div>
        <div className={`col-num ${headerProps(1).className}`.trim()} onClick={headerProps(1).onClick}>Min</div>
        <div className="col-team no-sort">Type</div>
        <div className="col-team no-sort">Body Part</div>
        <div className="col-team no-sort">Result</div>
      </div>
      {rows.map((r, i) => (
        <div key={r.key} data-sort-key={r.key} className="data-row">
          <div className="col-rank">{i + 1}</div>
          <div className="col-team">{r.opposition}</div>
          <div className="col-num" data-val={r.minute}>{r.clock}</div>
          <div className="col-team">{r.type}</div>
          <div className="col-team">{r.bodyPart}</div>
          <div className="col-team">{r.result}</div>
        </div>
      ))}
    </div>
  );
}
